import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material'
import PropTypes from 'prop-types'

/**
 * TbxAlertDialog - A confirmation dialog with customizable buttons and loading state
 *
 * @param {Object} props
 * @param {string} [props.buttonVariant='text'] - MUI Button variant for dialog buttons
 * @param {string} [props.cancelButtonText='Cancel'] - Text for the cancel button
 * @param {string|Object} props.content - Dialog content text or component
 * @param {boolean} [props.hideCancelButton=false] - Hide the cancel button
 * @param {boolean} [props.hideOkButton=false] - Hide the OK button
 * @param {boolean} [props.loading=false] - Show loading state with disabled buttons
 * @param {string} [props.loadingText] - Optional text to display during loading
 * @param {string} [props.okButtonText='Ok'] - Text for the OK button
 * @param {Function} props.onCancelClose - Called when cancel button is clicked
 * @param {Function} props.onOkClose - Called when OK button is clicked
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} props.title - Dialog title
 * @returns {JSX.Element}
 */
const TbxAlertDialog = ({
    buttonVariant = 'text',
    cancelButtonText = 'Cancel',
    content,
    hideCancelButton = false,
    hideOkButton = false,
    loading = false,
    loadingText,
    okButtonText = 'Ok',
    onCancelClose,
    onOkClose,
    open,
    title,
    ...rest
}) => {
    return (
        <Dialog
            aria-describedby="alert-dialog-description"
            aria-labelledby="alert-dialog-title"
            open={open}
            onClose={loading ? undefined : onCancelClose}
            {...rest}
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {loading && loadingText ? (
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
                        <CircularProgress size={20} />
                        <DialogContentText id="alert-dialog-description">{loadingText}</DialogContentText>
                    </Box>
                ) : (
                    <DialogContentText id="alert-dialog-description">{content}</DialogContentText>
                )}
            </DialogContent>
            <DialogActions sx={{ gap: 1, padding: 2 }}>
                {!hideCancelButton ? (
                    <Button
                        color="secondary"
                        disabled={loading}
                        variant={buttonVariant}
                        autoFocus
                        onClick={onCancelClose}
                    >
                        {cancelButtonText}
                    </Button>
                ) : null}
                {!hideOkButton ? (
                    <Button
                        color="error"
                        disabled={loading}
                        startIcon={
                            loading ? (
                                <CircularProgress
                                    color="inherit"
                                    size={16}
                                />
                            ) : null
                        }
                        variant={buttonVariant}
                        onClick={onOkClose}
                    >
                        {okButtonText}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    )
}

TbxAlertDialog.propTypes = {
    buttonVariant: PropTypes.oneOf(['text', 'contained', 'outlined']),
    cancelButtonText: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    hideCancelButton: PropTypes.bool,
    hideOkButton: PropTypes.bool,
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    okButtonText: PropTypes.string,
    open: PropTypes.bool,
    title: PropTypes.string,
    onCancelClose: PropTypes.func,
    onOkClose: PropTypes.func,
}

export default TbxAlertDialog
